"use client"

import * as React from "react"

import { Button } from "./Button"
import { cn } from "./cn"

/**
 * A dialog over the page: the dimmed backdrop, the card, its heading with a
 * close button, and a scrolling body. Escape and a click on the backdrop close
 * it. The frame is the kit's; what the body shows stays with the module.
 *
 * The module decides whether it is open by rendering it or not, and passes the
 * close label, because the kit carries no text of its own.
 */
function Dialog({
  title,
  onClose,
  closeLabel,
  className,
  children
}: {
  title: React.ReactNode
  onClose: () => void
  closeLabel: string
  className?: string
  children: React.ReactNode
}) {
  const titleId = React.useId()

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [onClose])

  return (
    <div
      data-slot="dialog-backdrop"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={event => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-slot="dialog"
        className={cn(
          "flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-white shadow-xl",
          "avex:rounded-avex-lg avex:bg-surface avex:shadow-avex-md",
          className
        )}
      >
        <header className="flex items-center justify-between gap-3 border-b border-gray-100 px-6 py-4 avex:border-line">
          <h2 id={titleId} className="min-w-0 text-lg font-bold text-gray-900 avex:text-base avex:font-semibold avex:text-ink">
            {title}
          </h2>
          <Button variant="ghost" size="icon" className="size-8 rounded-full text-lg" aria-label={closeLabel} onClick={onClose}>
            <span aria-hidden>×</span>
          </Button>
        </header>
        <div className="flex flex-col gap-6 overflow-y-auto px-6 py-4">{children}</div>
      </div>
    </div>
  )
}

export { Dialog }
